import { useState } from 'react';
import { GraduationCap, ArrowLeft, ArrowRight, SkipForward, Plus, CheckCircle } from 'lucide-react';

export default function Step4_Students({ onNext, onBack, onSubmit, loading }) {
  const [option, setOption] = useState('skip'); // 'skip' or 'add'

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSubmit();
      if (option === 'skip') {
        onNext();
      } else {
        // Navigate to student creation page
        window.location.href = '/students/add';
      }
    } catch (err) {
      // Error handled in parent
    }
  };

  return (
    <div>
      <div className="flex items-center mb-6">
        <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mr-4">
          <GraduationCap className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Students Setup</h2>
          <p className="text-gray-600">Add students to your school</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Option Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Skip Option */}
          <button
            type="button"
            onClick={() => setOption('skip')}
            className={`p-6 rounded-xl border-2 transition-all text-left ${
              option === 'skip'
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-300 hover:border-blue-400'
            }`}
          >
            <div className="flex items-center mb-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                option === 'skip' ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>
                <SkipForward className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold">Skip for Now</h3>
            </div>
            <p className="text-gray-600 text-sm">
              You can add students later from the Students section. This will complete the setup process.
            </p>
          </button>

          {/* Add Students Option */}
          <button
            type="button"
            onClick={() => setOption('add')}
            className={`p-6 rounded-xl border-2 transition-all text-left ${
              option === 'add'
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-300 hover:border-blue-400'
            }`}
          >
            <div className="flex items-center mb-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                option === 'add' ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>
                <Plus className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold">Add Students Now</h3>
            </div>
            <p className="text-gray-600 text-sm">
              Add students immediately using the student creation form. This will take you to the Students section.
            </p>
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> You can always add, edit, or remove students later from the Student Management section.
          </p>
        </div>

        {/* Completion Message */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-green-800 font-semibold mb-1">
              Almost Done!
            </p>
            <p className="text-sm text-green-700">
              After this step, your school setup will be complete. You can access all features from the dashboard.
            </p>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? 'Completing...' : option === 'skip' ? 'Complete Setup' : 'Add Students'}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
}
